import * as R from 'ramda';
import { getProfileExportToken } from 'modules/jwt-2';
import { track } from './tracking-utils';
import { profileExportUrl } from './from-hub.js';

// Guards against double-clicks minting twice / opening multiple tabs while a
// token mint is in flight.
let inFlight = false;

const buildTarget = jwt => {
  const target = new URL('/', profileExportUrl);
  target.hash = new URLSearchParams({ token: jwt, hub: window.location.origin }).toString();
  return target.toString();
};

const openTarget = (exportTab, url) => {
  if (exportTab) {
    exportTab.location = url;
  } else {
    // The synchronous open was blocked; last-ditch attempt.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const failureKind = (status, reason) => {
  if (status === 401 && reason === 'no consent') return 'consent';
  if (status === 401) return 'signin';
  return 'generic';
};

const handleFailure = (e, t, exportTab) => {
  if (exportTab) exportTab.close();
  const status = R.path(['response', 'status'], e);
  const kind = failureKind(status, R.path(['response', 'data', 'reason'], e));
  if (kind === 'consent') {
    window.alert(t('Please accept the terms and conditions before exporting your profile.'));
  } else if (kind === 'signin') {
    window.location.href = '/users/sign_in';
  } else {
    // Surface config/network failures to analytics so a bad PROFILE_EXPORT_URL
    // isn't silently swallowed behind a generic alert.
    track('AdvocateNav::Profile_Export_Failed', { status: R.defaultTo(null, status) });
    window.alert(t('Could not open profile export. Please try again.'));
  }
};

// Mints a fresh short-lived Passport export token and opens the external
// Passport export app with the token + hub origin in the URL hash (kept out of
// server logs). See plan: passport_profile_export_link.
export const openProfileExport = async t => {
  if (inFlight) return;
  inFlight = true;

  // Open the tab synchronously, still inside the click gesture, so popup
  // blockers don't kill it once the async token mint breaks the gesture.
  // We can't use `noopener` here (it forces a null handle we need to redirect),
  // so we sever `opener` manually to keep the reverse-tabnabbing protection.
  const exportTab = window.open('', '_blank');
  if (exportTab) exportTab.opener = null;

  try {
    openTarget(exportTab, buildTarget(await getProfileExportToken()));
  } catch (e) {
    handleFailure(e, t, exportTab);
  } finally {
    inFlight = false;
  }
};
